import React, { useState, useEffect, useCallback } from 'react';
import { Badge, BlockStack, Button, Card, DataTable, InlineStack, Link, Pagination, Select, Spinner, Tabs, Text, TextField } from '@shopify/polaris';
import { formatDate, formatToCurrency } from '../../../shared/components/functions';

function formatQuantity(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '-';
  if (Number.isInteger(numericValue)) return String(numericValue);
  return numericValue
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');
}

function CustomerCell({ customerId, customerName }) {
  if (!customerId && !customerName) return '-';

  return (
    <BlockStack gap="050">
      {customerId ? (
        <Link removeUnderline url={`/companies/${customerId}`}>
          {customerName || `Customer #${customerId}`}
        </Link>
      ) : (
        <Text as="span">{customerName}</Text>
      )}
      {customerId ? (
        <Text as="span" tone="subdued" variant="bodySm">
          #{customerId}
        </Text>
      ) : null}
    </BlockStack>
  );
}

function SummaryBadges({ totalCount, totalQuantity }) {
  return (
    <InlineStack gap="300" align="center">
      <Badge tone="info">{totalCount} lines</Badge>
      <Badge>{formatQuantity(totalQuantity)} pcs</Badge>
    </InlineStack>
  );
}

function getSaleStatusBadge(statusText) {
  switch (statusText) {
    case 'Paid':
      return (
        <Badge tone="success" size="small">
          {statusText}
        </Badge>
      );
    case 'Awaiting payment':
      return (
        <Badge tone="attention" size="small">
          {statusText}
        </Badge>
      );
    case 'Partially invoiced':
      return (
        <Badge tone="warning" size="small">
          {statusText}
        </Badge>
      );
    case 'Canceled':
      return (
        <Badge tone="critical" size="small">
          {statusText}
        </Badge>
      );
    default:
      return <Badge size="small">{statusText || 'Not invoiced'}</Badge>;
  }
}

function getInvoiceStatusBadge(status, statusText) {
  const text = statusText || status;
  if (status === 0) return <Badge size="small">{text}</Badge>;
  if (status === 9) {
    return (
      <Badge tone="success" size="small">
        {text}
      </Badge>
    );
  }
  if (status === 1) {
    return (
      <Badge tone="attention" size="small">
        {text}
      </Badge>
    );
  }
  if (status === 2) {
    return (
      <Badge tone="warning" size="small">
        {text}
      </Badge>
    );
  }
  return (
    <Badge tone="critical" size="small">
      {text}
    </Badge>
  );
}

function SaleOrderLinesTable({ productId }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('50');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ customerName: '', customerId: '' });

  const fetchData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const limit = parseInt(pageSize, 10);
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams({
        productId: String(productId),
        limit: String(limit),
        offset: String(offset)
      });

      if (appliedFilters.customerName) params.append('customerName', appliedFilters.customerName);
      if (appliedFilters.customerId) params.append('customerId', appliedFilters.customerId);

      const countParams = new URLSearchParams({ productId: String(productId) });
      if (appliedFilters.customerName) countParams.append('customerName', appliedFilters.customerName);
      if (appliedFilters.customerId) countParams.append('customerId', appliedFilters.customerId);

      const countResponse = await fetch(`/api/metrics/product-sale-lines-count?${countParams}`);
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalCount(countData.count || 0);
        setTotalQuantity(Number(countData.totalQuantity) || 0);
      } else {
        setTotalCount(0);
        setTotalQuantity(0);
      }

      const response = await fetch(`/api/metrics/product-sale-lines?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLines(Array.isArray(data) ? data : []);
      } else {
        setLines([]);
      }
    } catch (err) {
      console.error('Error fetching product sale lines:', err);
      setLines([]);
      setTotalCount(0);
      setTotalQuantity(0);
    }
    setLoading(false);
  }, [appliedFilters, currentPage, pageSize, productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedFilters({ customerName: customerNameFilter, customerId: customerIdFilter });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setCustomerNameFilter('');
    setCustomerIdFilter('');
    setAppliedFilters({ customerName: '', customerId: '' });
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyFilters();
  };

  const totalPages = Math.ceil(totalCount / parseInt(pageSize, 10));
  const hasActiveFilters = appliedFilters.customerName || appliedFilters.customerId;

  const rows = lines.map((item) => [
    <Link removeUnderline url={`/sales/${item.OrderbevestigingID}`}>
      {item.Orderbevestigingnummer}
    </Link>,
    item.Datum ? formatDate(item.Datum) : '-',
    <CustomerCell customerId={item.CustomerID} customerName={item.CustomerName} />,
    <Text variant="bodyMd" as="span" truncate>
      {item.tekstlijn || item.ProductName || '-'}
    </Text>,
    formatQuantity(item.aantal),
    item.VP !== null && item.VP !== undefined ? formatToCurrency(item.VP) : '-',
    item.line_total !== null && item.line_total !== undefined ? formatToCurrency(item.line_total) : '-',
    getSaleStatusBadge(item.statusText),
    item.RefKlant || '-'
  ]);

  const pageSizeOptions = [
    { label: '25 per page', value: '25' },
    { label: '50 per page', value: '50' },
    { label: '100 per page', value: '100' },
    { label: '250 per page', value: '250' }
  ];

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" wrap={false}>
        <div>
          <Text variant="headingMd" as="h3">
            Sale Order Lines
          </Text>
          <Text tone="subdued" as="p">
            This product sold to customers, newest first.
          </Text>
        </div>
        <InlineStack gap="300" align="center">
          <Select
            label="Page size"
            labelHidden
            options={pageSizeOptions}
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <SummaryBadges totalCount={totalCount} totalQuantity={totalQuantity} />
        </InlineStack>
      </InlineStack>

      <div onKeyDown={handleKeyDown}>
        <InlineStack gap="300" align="start" blockAlign="end">
          <div style={{ width: '240px' }}>
            <TextField
              label="Customer name"
              value={customerNameFilter}
              onChange={setCustomerNameFilter}
              placeholder="Search customer..."
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setCustomerNameFilter('')}
            />
          </div>
          <div style={{ width: '160px' }}>
            <TextField
              label="Customer ID"
              value={customerIdFilter}
              onChange={setCustomerIdFilter}
              placeholder="e.g. 12345"
              autoComplete="off"
              type="number"
              clearButton
              onClearButtonClick={() => setCustomerIdFilter('')}
            />
          </div>
          <div style={{ paddingTop: '24px' }}>
            <InlineStack gap="200">
              <Button onClick={handleApplyFilters} variant="primary">
                Apply filters
              </Button>
              {hasActiveFilters ? (
                <Button onClick={handleClearFilters} variant="plain">
                  Clear
                </Button>
              ) : null}
            </InlineStack>
          </div>
        </InlineStack>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      ) : lines.length > 0 ? (
        <>
          <DataTable
            verticalAlign="middle"
            stickyHeader={true}
            increasedTableDensity={true}
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
            headings={[<b>Sale #</b>, <b>Date</b>, <b>Customer</b>, <b>Line Text</b>, <b>Qty</b>, <b>Unit Price</b>, <b>Line Total</b>, <b>Status</b>, <b>Customer Ref</b>]}
            rows={rows}
          />
          {totalPages > 1 ? (
            <InlineStack align="center" gap="400">
              <Pagination hasPrevious={currentPage > 1} onPrevious={() => setCurrentPage(currentPage - 1)} hasNext={currentPage < totalPages} onNext={() => setCurrentPage(currentPage + 1)} />
              <Text as="span" tone="subdued">
                Page {currentPage} of {totalPages}
              </Text>
            </InlineStack>
          ) : null}
        </>
      ) : (
        <Text as="p" tone="subdued">
          No sale order lines found for this product.
        </Text>
      )}
    </BlockStack>
  );
}

function InvoiceLinesTable({ productId }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('50');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ customerName: '', customerId: '' });

  const fetchData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const limit = parseInt(pageSize, 10);
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams({
        productId: String(productId),
        limit: String(limit),
        offset: String(offset)
      });

      if (appliedFilters.customerName) params.append('customerName', appliedFilters.customerName);
      if (appliedFilters.customerId) params.append('customerId', appliedFilters.customerId);

      const countParams = new URLSearchParams({ productId: String(productId) });
      if (appliedFilters.customerName) countParams.append('customerName', appliedFilters.customerName);
      if (appliedFilters.customerId) countParams.append('customerId', appliedFilters.customerId);

      const countResponse = await fetch(`/api/metrics/product-invoice-line-details-count?${countParams}`);
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalCount(countData.count || 0);
        setTotalQuantity(Number(countData.totalQuantity) || 0);
      } else {
        setTotalCount(0);
        setTotalQuantity(0);
      }

      const response = await fetch(`/api/metrics/product-invoice-line-details?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLines(Array.isArray(data) ? data : []);
      } else {
        setLines([]);
      }
    } catch (err) {
      console.error('Error fetching product invoice lines:', err);
      setLines([]);
      setTotalCount(0);
      setTotalQuantity(0);
    }
    setLoading(false);
  }, [appliedFilters, currentPage, pageSize, productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedFilters({ customerName: customerNameFilter, customerId: customerIdFilter });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setCustomerNameFilter('');
    setCustomerIdFilter('');
    setAppliedFilters({ customerName: '', customerId: '' });
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyFilters();
  };

  const totalPages = Math.ceil(totalCount / parseInt(pageSize, 10));
  const hasActiveFilters = appliedFilters.customerName || appliedFilters.customerId;

  const rows = lines.map((item) => [
    <Link removeUnderline url={`/invoices/${item.faktuurnr}`}>
      {item.Factuurnummer}
    </Link>,
    item.datum ? formatDate(item.datum) : '-',
    <CustomerCell customerId={item.CustomerID} customerName={item.CustomerName} />,
    <Text variant="bodyMd" as="span" truncate>
      {item.tekstlijn || item.ProductName || '-'}
    </Text>,
    formatQuantity(item.aantal),
    item.VP !== null && item.VP !== undefined ? formatToCurrency(item.VP) : '-',
    item.line_total !== null && item.line_total !== undefined ? formatToCurrency(item.line_total) : '-',
    getInvoiceStatusBadge(item.status, item.statusText),
    item.refklant || '-'
  ]);

  const pageSizeOptions = [
    { label: '25 per page', value: '25' },
    { label: '50 per page', value: '50' },
    { label: '100 per page', value: '100' },
    { label: '250 per page', value: '250' }
  ];

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" wrap={false}>
        <div>
          <Text variant="headingMd" as="h3">
            Invoice Lines
          </Text>
          <Text tone="subdued" as="p">
            This product invoiced to customers, newest first.
          </Text>
        </div>
        <InlineStack gap="300" align="center">
          <Select
            label="Page size"
            labelHidden
            options={pageSizeOptions}
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <SummaryBadges totalCount={totalCount} totalQuantity={totalQuantity} />
        </InlineStack>
      </InlineStack>

      <div onKeyDown={handleKeyDown}>
        <InlineStack gap="300" align="start" blockAlign="end">
          <div style={{ width: '240px' }}>
            <TextField
              label="Customer name"
              value={customerNameFilter}
              onChange={setCustomerNameFilter}
              placeholder="Search customer..."
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setCustomerNameFilter('')}
            />
          </div>
          <div style={{ width: '160px' }}>
            <TextField
              label="Customer ID"
              value={customerIdFilter}
              onChange={setCustomerIdFilter}
              placeholder="e.g. 12345"
              autoComplete="off"
              type="number"
              clearButton
              onClearButtonClick={() => setCustomerIdFilter('')}
            />
          </div>
          <div style={{ paddingTop: '24px' }}>
            <InlineStack gap="200">
              <Button onClick={handleApplyFilters} variant="primary">
                Apply filters
              </Button>
              {hasActiveFilters ? (
                <Button onClick={handleClearFilters} variant="plain">
                  Clear
                </Button>
              ) : null}
            </InlineStack>
          </div>
        </InlineStack>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      ) : lines.length > 0 ? (
        <>
          <DataTable
            verticalAlign="middle"
            stickyHeader={true}
            increasedTableDensity={true}
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
            headings={[<b>Invoice #</b>, <b>Date</b>, <b>Customer</b>, <b>Line Text</b>, <b>Qty</b>, <b>Unit Price</b>, <b>Line Total</b>, <b>Status</b>, <b>Customer Ref</b>]}
            rows={rows}
          />
          {totalPages > 1 ? (
            <InlineStack align="center" gap="400">
              <Pagination hasPrevious={currentPage > 1} onPrevious={() => setCurrentPage(currentPage - 1)} hasNext={currentPage < totalPages} onNext={() => setCurrentPage(currentPage + 1)} />
              <Text as="span" tone="subdued">
                Page {currentPage} of {totalPages}
              </Text>
            </InlineStack>
          ) : null}
        </>
      ) : (
        <Text as="p" tone="subdued">
          No invoice lines found for this product.
        </Text>
      )}
    </BlockStack>
  );
}

export default function ProductSalesHistory({ productId }) {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    {
      id: 'sale-lines',
      content: 'Sale Order Lines',
      panelID: 'product-sale-lines-panel'
    },
    {
      id: 'invoice-lines',
      content: 'Invoice Lines',
      panelID: 'product-invoice-lines-panel'
    }
  ];

  const handleTabChange = useCallback((selectedTabIndex) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  return (
    <Card>
      <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} fitted>
        <div style={{ paddingTop: '16px' }}>
          {selectedTab === 0 ? <SaleOrderLinesTable productId={productId} /> : null}
          {selectedTab === 1 ? <InvoiceLinesTable productId={productId} /> : null}
        </div>
      </Tabs>
    </Card>
  );
}
